import { Input } from '../ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select';
import { MagnifyingGlass } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { STATUS_TABS } from '../../utils/myListingsHelpers';

const TAB_LABEL_KEYS = {
  all: 'myListings.statusAll',
  active: 'listing.status.active',
  pending: 'myListings.statusPending',
  expiring: 'myListings.filterExpiring',
  expired: 'listing.status.expired',
  deleted: 'myListings.statusDeleted',
};

const SORT_OPTIONS = [
  { value: 'newest', labelKey: 'listing.sortNewest' },
  { value: 'oldest', labelKey: 'listing.sortOldest' },
  { value: 'price_asc', labelKey: 'listing.priceLowHigh' },
  { value: 'price_desc', labelKey: 'listing.priceHighLow' },
  { value: 'expiring', labelKey: 'listing.sortExpiringSoon' },
  { value: 'most_viewed', labelKey: 'listing.sortMostViewed' },
];

/**
 * Toolbar for My Listings.
 *
 * NOTE: the sub-renderers below are plain functions returning JSX (NOT nested
 * React components). Declaring components inside the render body gives them a
 * new type on every render, which remounts the search <Input> after every
 * keystroke and drops focus. Keep them as functions.
 */
export const MyListingsToolbar = ({
  statusTab, onStatusTabChange, counts,
  category, onCategoryChange, categoryOptions,
  sortBy, onSortChange,
  search, onSearchChange,
}) => {
  const { t } = useTranslation();

  const renderStatusSelect = (mobile) => (
    <Select value={statusTab} onValueChange={onStatusTabChange}>
      <SelectTrigger className={mobile ? 'min-h-11' : ''} data-testid={mobile ? 'my-listings-status-select-mobile' : 'my-listings-status-select'}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {STATUS_TABS.map((tab) => (
          <SelectItem key={tab} value={tab} data-testid={`status-option-${tab}`}>
            {t(TAB_LABEL_KEYS[tab])} ({counts[tab] || 0})
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderSortSelect = (mobile) => (
    <Select value={sortBy} onValueChange={onSortChange}>
      <SelectTrigger className={mobile ? 'min-h-11' : ''} data-testid={mobile ? 'my-listings-sort-select-mobile' : 'my-listings-sort-select'}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        {SORT_OPTIONS.map((opt) => (
          <SelectItem key={opt.value} value={opt.value} data-testid={`sort-option-${opt.value}`}>
            {t(opt.labelKey)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderCategorySelect = (mobile) => (
    <Select value={category} onValueChange={onCategoryChange}>
      <SelectTrigger className={mobile ? 'min-h-11' : ''} data-testid={mobile ? 'my-listings-category-select-mobile' : 'my-listings-category-select'}>
        <SelectValue />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="all" data-testid="category-option-all">{t('myListings.allCategories')}</SelectItem>
        {categoryOptions.map((c) => (
          <SelectItem key={c.value} value={c.value} data-testid={`category-option-${c.value}`}>{c.label}</SelectItem>
        ))}
      </SelectContent>
    </Select>
  );

  const renderSearchInput = (mobile) => (
    <div className="relative flex-1">
      <MagnifyingGlass size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-text_muted" />
      <Input
        value={search}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder={t('myListings.searchPlaceholder')}
        className={`border border-border pl-9 ${mobile ? 'min-h-11' : ''}`}
        data-testid={mobile ? 'my-listings-search-mobile' : 'my-listings-search'}
      />
    </div>
  );

  return (
    <div className="mb-4" data-testid="my-listings-toolbar">
      {/* Desktop: status pills row + search/category/sort row */}
      <div className="hidden md:block space-y-3">
        <div className="flex gap-2 flex-wrap" data-testid="status-tabs-desktop">
          {STATUS_TABS.map((tab) => (
            <button
              key={tab}
              onClick={() => onStatusTabChange(tab)}
              className={`px-3.5 py-1.5 rounded-full text-sm font-medium border transition-colors whitespace-nowrap ${
                statusTab === tab
                  ? 'bg-primary text-white border-primary'
                  : 'bg-white text-text_muted border-border hover:border-primary hover:text-primary'
              }`}
              data-testid={`status-tab-${tab}`}
            >
              {t(TAB_LABEL_KEYS[tab])} ({counts[tab] || 0})
            </button>
          ))}
        </div>
        <div className="flex gap-3 items-center">
          {renderSearchInput(false)}
          <div className="w-56">{renderCategorySelect(false)}</div>
          <div className="w-56">{renderSortSelect(false)}</div>
        </div>
      </div>

      {/* Mobile: status + sort dropdowns, then search, then category */}
      <div className="md:hidden space-y-3">
        <div className="grid grid-cols-2 gap-2">
          {renderStatusSelect(true)}
          {renderSortSelect(true)}
        </div>
        <div className="flex gap-2">
          <div className="flex-1">{renderSearchInput(true)}</div>
        </div>
        {renderCategorySelect(true)}
      </div>
    </div>
  );
};
